import { WaypointStatusName, WaypointType } from '@caresend/types';
import { ButtonStyle } from '../../../../../../buttons/ButtonComponent/model.js';
import { extractTarget } from '../../helpers/helpers.js';
import { getCommittedWorkflow, existingWorkflowMap, getWorkflowItemInitializer, maybeAddToMap, sortWorkflowByPriority } from '../helpers.js';
import { WaypointWorkflowItemID, WaypointActionWorkflowContextID } from './model.js';
import { WorkflowModalComponentName, WorkflowItemModalButtonAction, WorkflowInlineComponentName } from '../model.js';

/** Modal meta variants for the confirm departure workflow item */
var ConfirmDepartureModalMetaVariants;
(function (ConfirmDepartureModalMetaVariants) {
    ConfirmDepartureModalMetaVariants["WITHIN_BIG_WINDOW"] = "withinBigWindow";
    ConfirmDepartureModalMetaVariants["OUTSIDE_BIG_WINDOW"] = "outsideBigWindow";
})(ConfirmDepartureModalMetaVariants || (ConfirmDepartureModalMetaVariants = {}));
const getWaypointWorkflow = (wp, contextID = 'default', parentReferenceID, { waypoints }) => {
    if (!waypoints)
        return [];
    const waypoint = extractTarget(wp, waypoints.getRawClone);
    if (!waypoint)
        return [];
    const priorities = {};
    const workflow = getCommittedWorkflow(waypoint, parentReferenceID, contextID);
    const workflowMap = existingWorkflowMap(workflow);
    const initWorkflowItem = getWorkflowItemInitializer(waypoint.id, 'waypoints', {
        context: contextID,
        referenceID: parentReferenceID,
        storeRef: 'waypoints',
    });
    if (waypoints.getters.hasMailInAction(waypoint)) {
        const hasMailInAction = initWorkflowItem(WaypointWorkflowItemID.MAIL_IN_PACKING_NON_BLOCKING, {
            nonBlocking: true,
        }, {
            context: WaypointActionWorkflowContextID.MAIL_IN_PACKING,
            key: 'waypointActions',
        });
        priorities[hasMailInAction.id] = 1;
        maybeAddToMap(hasMailInAction, workflowMap);
    }
    /** Departure workflow item */
    const departure = initWorkflowItem(WaypointWorkflowItemID.DEPARTURE, {
        after: {
            status: {
                status: WaypointStatusName.TRANSIT_WAYPOINT,
            },
        },
        modal: {
            buttonProps: {
                label: 'Ready to drive?',
            },
            manualTrigger: true,
            name: WorkflowModalComponentName.CONFIRM_DEPARTURE,
            modalMetaVariants: {
                [ConfirmDepartureModalMetaVariants.OUTSIDE_BIG_WINDOW]: {
                    title: 'Too early to drive',
                },
                [ConfirmDepartureModalMetaVariants.WITHIN_BIG_WINDOW]: {
                    title: 'Too early to drive',
                    footerButtons: [
                        {
                            label: 'Back to my itinerary',
                            action: WorkflowItemModalButtonAction.CLOSE,
                        },
                        {
                            label: 'Drive',
                            action: WorkflowItemModalButtonAction.COMPLETE,
                            buttonStyle: ButtonStyle.OUTLINE,
                        },
                    ],
                },
            },
            props: {
                waypointID: waypoint.id,
            },
        },
    });
    priorities[departure.id] = 2;
    maybeAddToMap(departure, workflowMap);
    /** Arrival workflow item */
    const arrival = initWorkflowItem(WaypointWorkflowItemID.ARRIVAL, {
        after: {
            status: {
                status: WaypointStatusName.AT_WAYPOINT,
            },
        },
        component: {
            name: WorkflowInlineComponentName.GO_TO_PATIENT,
            props: {
                waypointID: waypoint.id,
            },
        },
    });
    priorities[arrival.id] = 3;
    maybeAddToMap(arrival, workflowMap);
    /** ID card workflow item */
    if (waypoint.type === WaypointType.PATIENT) {
        const idCard = initWorkflowItem(WaypointWorkflowItemID.ID_CARD, {
            modal: {
                buttonProps: {
                    label: 'Show ID badge',
                },
                name: WorkflowModalComponentName.ID_CARD,
                completeOnClose: true,
                title: 'CareSend ID',
            },
        });
        priorities[idCard.id] = 4;
        maybeAddToMap(idCard, workflowMap);
    }
    if (waypoints.getters.hasMailInAction(waypoint)) {
        const hasMailInAction = initWorkflowItem(WaypointWorkflowItemID.MAIL_IN_PACKING_BLOCKING, undefined, {
            context: WaypointActionWorkflowContextID.MAIL_IN_PACKING,
            key: 'waypointActions',
        });
        priorities[hasMailInAction.id] = 5;
        maybeAddToMap(hasMailInAction, workflowMap);
    }
    /** Waypoint actions workflow item */
    const waypointActions = initWorkflowItem(WaypointWorkflowItemID.WAYPOINT_ACTIONS, {
        after: {
            status: {
                status: WaypointStatusName.COMPLETE,
                /** Accurate timestamp is handled upon completion */
                timestamp: 0,
            },
        },
        afterFailure: {
            status: {
                status: WaypointStatusName.COMPLETE,
            },
        },
    }, {
        context: WaypointActionWorkflowContextID.DEFAULT,
        key: 'waypointActions',
    });
    priorities[waypointActions.id] = 6;
    maybeAddToMap(waypointActions, workflowMap);
    return sortWorkflowByPriority(Array.from(workflowMap.values()), priorities);
};

export { ConfirmDepartureModalMetaVariants, getWaypointWorkflow };
